import { animate, style, transition, trigger } from '@angular/animations';

import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { FilledButtonComponent, OutlinedButtonComponent } from '@custom-components/general/button';
import {
  InitialConfigFileDetailsModel,
  InitialConfigFileVersion,
} from '@core/models/setting.model';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsStore } from '@root/app/store/settings.store';
import { CompanyStore } from '@root/app/store/company.store';

@Component({
  selector: 'xemex-initial-config-file-details-dialog',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    TranslateModule,
    SkeletonBlockComponent,
    MatProgressSpinnerModule,
    FilledButtonComponent,
    OutlinedButtonComponent,
  ],
  standalone: true,
  templateUrl: './initial-config-file-details-dialog.component.html',
  styleUrl: './initial-config-file-details-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('versionChange', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('100ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class InitialConfigFileDetailsDialogComponent {
  readonly dialogRef = inject(MatDialogRef<InitialConfigFileDetailsDialogComponent>);
  settingsStore = inject(SettingsStore);
  companyStore = inject(CompanyStore);
  data = inject(MAT_DIALOG_DATA);

  /** Partner names resolved from the version's partner OIDs (never shows raw OIDs). */
  get partnerNames(): string {
    const cache = this.companyStore.partnerNameCache();
    const names = (this.currentFileVersion?.partnerOids ?? [])
      .map(oid => cache[oid])
      .filter(Boolean);
    return names.length ? names.join(', ') : '-';
  }

  configFileDetails: InitialConfigFileDetailsModel | undefined;
  currentFileVersion: InitialConfigFileVersion | undefined;
  isLoadingConfigFileDetails = false;
  isDeletingFile = false;
  isDownloadingFile = false;
  showChannels = false;
  showLoadProfiles = false;
  versionsColumns = ['versionId', 'isDeleted', 'createdOn', 'createdBy'];

  constructor() {
    if (this.data.fileId) {
      this.settingsStore.getInitialConfigFileDetails(this.data.fileId);
    }
    effect(() => {
      this.configFileDetails = this.settingsStore.initialConfigFileDetails();
      const targetVersionId = this.data.versionId;
      this.currentFileVersion =
        (targetVersionId
          ? this.configFileDetails?.versions.find(v => v.versionId === targetVersionId)
          : undefined) ?? this.configFileDetails?.versions.at(-1);
      // Resolve the version's partner OIDs to names for display.
      (this.currentFileVersion?.partnerOids ?? []).forEach(oid =>
        this.companyStore.getPartnerByIdMethod(oid),
      );
      this.isLoadingConfigFileDetails = this.settingsStore.isLoadingInitialConfigFileDetails();
      this.isDeletingFile = this.settingsStore.isDeletingInitialConfigFile();
    });
  }

  onDownload() {
    if (this.currentFileVersion) {
      this.settingsStore.getICFContentByVersionId(this.currentFileVersion.versionId);
    }
  }

  onVersionClicked(versionId: number | undefined) {
    if (!versionId) return;
    const version = this.configFileDetails?.versions.find(v => v.versionId === versionId);
    if (version) {
      this.currentFileVersion = version;
    }
  }

  onDelete() {
    this.settingsStore.deleteInitialConfigFile(this.data.fileId);
  }
}
